import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { darkTheme, lightTheme } from "../../../../theme/Theme";
import DropZone from "./DropZone";
import ImageList from "./ImageList";
import Header from "./Header";
import Details from "./Details";
import { Toggle } from "pepsico-ds";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { setDisplay } from "../../../../slices/adminPageSlice";
import PreviewModal from "./PreviewModal";
import axios from "../../../../API/API";
import PropTypes from "prop-types";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../utils/Toast/toast";
import { deleteImage } from "../../../../slices/imageSlice";
import { FILE_SERVICE_BASE_URL } from "../../../../constant/apiUrl";
import { deleteCuratorFile } from "../../../../API/curatorApi";

export default function Hero({
  images,
  setHeroData,
  slotNumber,
  populateData,
  setFetchData,
  fetchData,
}) {
  const isDarkTheme = useSelector((store) => store.theme);
  const [dropped, setDropped] = useState(false);
  const [preview, setPreview] = useState(false);
  const [description, setDescription] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [enable, setEnable] = useState(true);
  const dispatch = useDispatch();
  const detailsRef = useRef(null);
  useEffect(() => {
    dispatch(setDisplay(false));
  }, [dispatch]);
  const token = localStorage.getItem("auth_token");
  useEffect(() => {
    // Reset or update state if necessary when images prop changes
    if (images && images.length > 0) {
      setDropped(true);
    }
    if (populateData) {
      setEnable(populateData?.isEnabled);
      setDescription(populateData?.description);
    }
  }, [images, populateData]);

  const onDrop = (acceptedFiles) => {
    const newImage = acceptedFiles[0];
    const imageWithPreview = Object?.assign(newImage, {
      preview: URL.createObjectURL(newImage),
    });
    populateData.thumbnailFileId = undefined;
    setHeroData([imageWithPreview], null); // Update only images
    setDropped(true);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedImages = Array.from(images);
    const [removed] = reorderedImages.splice(result.source.index, 1);
    reorderedImages.splice(result.destination.index, 0, removed);
    populateData.thumbnailFileId = undefined;
    // Pass images back along with slotNumber to update the correct slot
    setHeroData(reorderedImages, slotNumber);
    setDropped(true);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [],
    },
    onDrop,
    maxFiles: 1, // Ensure only one file can be uploaded at a time
  });

  const deletThumbanil = async (id) => {
    try {
      // Delete the image from the server
      const response = await deleteCuratorFile(id);
      // If the image was successfully deleted from the server, also remove it from localStorage

      dispatch(deleteImage({ id }));

      return response;
    } catch (error) {
      console.error("Error deleting thumbnail:", error);
      throw error; // Rethrow or handle the error as needed
    }
  };

  const onReplace = async (updatedImages) => {
    let data;
    if (populateData.thumbnailFileId) {
      data = await deletThumbanil(populateData.thumbnailFileId);

      populateData.thumbnailFileId = undefined;
    }

    setHeroData(updatedImages, slotNumber); // Update heroData with updated images based on slotNumber
  };

  const handleDeleteClick = async (previewUrl) => {
    const updatedImages = images.filter(
      (image) => image.preview !== previewUrl
    );
    if (populateData.thumbnailFileId) {
      let data;
      data = await deletThumbanil(populateData.thumbnailFileId);
      if (data?.status === 200) {
        populateData.thumbnailFileId = undefined;
      }
    }

    showSuccessToast("Image Removed");
    setHeroData(updatedImages, slotNumber);
    setDropped(false); // Update the image list after deletion
  };

  const handleFormSubmit = (data) => {
    if (images.length === 0) {
      showErrorToast("Please Add a Image");
    } else {
      const newData = {
        headline: data.headline,
        buttonText: data.buttonText,
        description: description,
        descriptionLink: data.descriptionLink,
        images,
        isEnabled: enable,
        slotNumber: slotNumber,
        thumbnailFileId: populateData.thumbnailFileId,
      };
      setPreviewData(newData);
      setPreview(true);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "auto",
            padding: "var(--Spacing-spacing-24, 24px)",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "var(--Spacing-spacing-16, 16px)",
            flexShrink: 0,
            borderRadius: "var(--Corner-radius-effect-corner-2, 8px)",
            background: isDarkTheme
              ? darkTheme.cardBackgroundColorLayer1
              : lightTheme.cardBackgroundColorLayer1,
            boxShadow:
              "var(--Elevation-X-Elevation-01, 0px) var(--Elevation-Y-Elevation-01, 0px) var(--Elevation-Blur-Elevation-01, 24px) var(--Elevation-Spread-Elevation-01, 0px) var(--Elevation-Colors-Elevation-01, rgba(0, 92, 188, 0.08))",
          }}
        >
          <Header
            isDarkTheme={isDarkTheme}
            darkTheme={darkTheme}
            lightTheme={lightTheme}
            slotNumber={slotNumber}
          />
          <Box
            sx={{
              display: "flex",
              width: "100%",
              padding:
                "var(--Spacing-spacing-8, 8px) var(--Spacing-spacing-16, 16px)",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "var(--Spacing-spacing-24, 24px)",
              alignSelf: "stretch",
              borderRadius: "var(--Corner-radius-effect-corner-2, 8px)",
            }}
          >
            <Box sx={{ width: { xs: "100%", md: "400px" } }}>
              {dropped ? (
                <ImageList
                  images={images}
                  onDragEnd={onDragEnd}
                  onReplace={onReplace}
                  onDelete={handleDeleteClick}
                  data-testid="image-list"
                />
              ) : (
                <DropZone
                  onDrop={onDrop}
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                  isDarkTheme={isDarkTheme}
                />
              )}
            </Box>
            <Box sx={{ width: { xs: "100%", md: "100%", lg: "400px" } }}>
              <Details
                onFormSubmit={handleFormSubmit}
                images={images}
                description={description}
                setDescription={setDescription}
                ref={detailsRef}
                populateData={populateData}
                data-testid="details"
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Toggle
                label="Disable"
                className="flex flex-row gap-1"
                onUpdate={(value) => {
                  console.log(value, "-----value---");

                  setEnable(value);
                }}
                size="medium"
                value="Enable"
                variant="horizontal"
                isDark={isDarkTheme}
                checked={enable}
              />
              <Button
                sx={{
                  display: "flex",
                  padding: "4px 16px", // `var(--Spacing-spacing-02, 4px)` for top and bottom, `var(--Spacing-spacing-0, 0px)` for left and right
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "4px", // `var(--Spacing-spacing-02, 4px)`
                  cursor: "pointer",
                  textTransform: "none",
                  borderRadius: "var(--Corner-radius-effect-corner-3, 24px)",
                  border:
                    "var(--Border-border-1, 1px) solid var(--Tokens-Button-Outlined, #005CBC)",
                }}
                data-testid="submit"
                onClick={() => detailsRef.current?.triggerSubmit()} // Trigger form submit
              >
                <RemoveRedEyeOutlinedIcon
                  sx={{
                    fill: "#005CBC",
                    width: "20px",
                    height: "20px",
                  }}
                />
                <Typography
                  sx={{
                    color: "var(--Tokens-Button-Borderless, #005CBC)",
                    fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                    fontSize: "var(--Font-Size-Small, 14px)",
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: "var(--Line-Height-Small, 20px)",
                  }}
                >
                  Preview
                </Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      {preview && (
        <PreviewModal
          open={preview}
          setPreviewModal={setPreview}
          data={previewData}
          setFetchData={setFetchData}
          fetchData={fetchData}
        />
      )}
    </>
  );
}
Hero.propTypes = {
  images: PropTypes.array, // Array of images to be displayed
  setHeroData: PropTypes.func.isRequired, // Function to update hero data
  slotNumber: PropTypes.number.isRequired, // Slot number for the hero
  populateData: PropTypes.object, // Object containing populated data
  setFetchData: PropTypes.func, // Function to set fetch data
  fetchData: PropTypes.object, // Fetch data object
};

Hero.defaultProps = {
  populateData: {},
};
